import { useQuery } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { FiDownload } from 'react-icons/fi'
import { FaMinus } from 'react-icons/fa6'
import { getInvoice } from '../api/userApi'
import { handleDate } from '../handleDate'
import { Link } from 'react-router-dom'
import Pagination from './Pagination'
import { FaSearch } from 'react-icons/fa'

const Invoice = () => {
  document.title = 'Invoice | Dealstreetjournal'

  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['invoice', page],
    queryFn: () => getInvoice(page),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const totalPages = Math.ceil((data?.totalCount || 0) / 10)

  const handleMonth = (dat) => {
    const date = new Date(dat)
    const month = date
      .toLocaleDateString('default', {
        month: 'long',
      })
      .toLowerCase()

    return month
  }

  const filteredInvoice = useMemo(() => {
    if (!searchTerm) return data?.invoices || []

    const term = searchTerm.toLowerCase()
    return (
      data?.invoices?.filter((invoice) => {
        const safe = (val) => (val ? val.toString().toLowerCase() : '')
        return (
          safe(invoice.invoice).includes(term) ||
          safe(handleMonth(invoice.date)).includes(term)
        )
      }) || []
    )
  }, [searchTerm, data])

  if (isPending) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <img
          src="../images/spinner.png"
          alt="Loading"
          className="w-12 h-12 animate-spin mb-2 mix-blend-multiply"
        />
      </div>
    )
  }

  if (isError) {
    return <span>Error: {error.message}</span>
  }

  return (
    <div className="bg-white p-6 sm:p-8 rounded-lg shadow-[0_0_15px_rgba(0,0,0,0.15)] text-gray-800">
      <h2 className="text-3xl font-aptos-bold text-center mb-8 text-[#ff7010]">
        Invoice
      </h2>

      {/* Search and Filter Section */}
      <div className="w-full mx-auto mb-8">
        <div className="relative max-w-md mx-auto">
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by Invoice, Month."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-full focus:ring-2 focus:ring-[#ff7010] focus:border-transparent outline-none transition-all"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <thead>
            <tr className="bg-[#ff7010] text-white text-center">
              <th className="px-4 py-3 font-aptos-semibold border-r border-[#ffa45a]">
                Date
              </th>
              <th className="px-4 py-3 font-aptos-semibold border-r border-[#ffa45a]">
                Invoice No
              </th>
              <th className="px-4 py-3 font-aptos-semibold border-r border-[#ffa45a]">
                Details
              </th>
              <th className="px-4 py-3 font-aptos-semibold border-r border-[#ffa45a]">
                Amount
              </th>
              <th className="px-4 py-3 font-aptos-semibold text-center">
                Download
              </th>
            </tr>
          </thead>

          <tbody>
            {filteredInvoice?.map((row, i) => (
              <tr
                key={i}
                className={`transition-all duration-200 ${
                  i % 2 === 0 ? 'bg-slate-100' : 'bg-white'
                } hover:bg-orange-50`}
              >
                {/* Date */}
                <td className="px-4 py-4 font-aptos-semibold border-t border-gray-200 text-center">
                  {handleDate(row.date)}
                </td>

                {/* Invoice No */}
                <td className="px-4 py-4 font-aptos-semibold border-t border-gray-200 text-center text-[#333]">
                  {row.invoice}
                </td>

                {/* Details */}
                <td className="px-4 py-4 border-t border-gray-200">
                  <ul className="list-none space-y-2">
                    {row.products.map((d, j) => (
                      <li
                        key={j}
                        className="flex flex-wrap items-center gap-2 text-sm text-gray-700"
                      >
                        <span className="text-gray-600 font-aptos-regular">
                          {handleDate(d.date) === 'Invalid Date'
                            ? d.date
                            : handleDate(d.date)}
                        </span>

                        <FaMinus className="text-gray-400 text-xs" />

                        <span className="font-aptos-semibold text-gray-800">
                          {d.productName}
                        </span>

                        <span className="font-aptos-semibold text-gray-800">
                          ({d.insight})
                        </span>
                      </li>
                    ))}
                  </ul>
                </td>

                {/* amount */}
                <td className="px-4 py-4 font-aptos-semibold border-t border-gray-200 text-center text-[#333]">
                  ₹{row.amount}
                </td>

                {/* Download */}
                <td className="px-4 py-4 border-t border-gray-200 text-center">
                  <Link
                    to="/dealdashboard/pdf"
                    state={{
                      invoice: row.invoice,
                      products: row.products,
                      name: row.name,
                      amount: row.amount,
                      country: row.country,
                      email: row.email,
                      orderId: row.orderId,
                      phone: row.phone,
                      state: row.state,
                      date: row.date,
                    }}
                    className="flex items-center cursor-pointer justify-center mx-auto w-10 h-10 rounded-full hover:bg-[#ff7010]/10 text-[#ff7010] hover:text-[#e65c00] transition"
                    title="Download Invoice"
                  >
                    <FiDownload size={20} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} />
      </div>
    </div>
  )
}

export default Invoice
